/* Heritage LM Market V4. Shopify quantities are whole 10g units; shoppers use kg. */
(function (host, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!host || !host.document) return;
  host.HLMWeight = api;
  const boot = (scope) => {
    const collection = scope.querySelector('[data-kg-collection]');
    if (collection) {
      const safeUrl = api.weightCollectionUrl(host.location.href, collection.dataset.defaultSort);
      if (safeUrl !== host.location.href) { host.location.replace(safeUrl); return; }
    }
    scope.querySelectorAll('[data-kg-product]').forEach(api.initProduct);
    scope.querySelectorAll('[data-kg-cart]').forEach(api.initCart);
  };
  if (host.document.readyState === 'loading') host.document.addEventListener('DOMContentLoaded', () => boot(host.document));
  else boot(host.document);
  host.document.addEventListener('shopify:section:load', (event) => boot(event.target));
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const UNITS_PER_KG = 100;
  const MAX_UNITS = 1000000;
  function weightCollectionUrl(href, defaultSort) {
    const url = new URL(href);
    let changed = false;
    for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) {
      if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; }
    }
    if ((url.searchParams.get('sort_by') || defaultSort || '').startsWith('price-')) {
      url.searchParams.set('sort_by', 'title-ascending'); changed = true;
    }
    if (changed) url.searchParams.delete('page');
    return changed ? url.href : href;
  }
  const positiveInt = (value, fallback) => {
    const n = Number(value);
    return Number.isSafeInteger(n) && n > 0 ? n : fallback;
  };
  function formatKg(units) {
    if (!Number.isSafeInteger(units) || units < 0) throw new Error('Invalid weight quantity.');
    return (units / UNITS_PER_KG).toFixed(2).replace(/\.?0+$/, '');
  }
  function parseKg(value, rules) {
    const rule = Object.assign({ min: 1, max: MAX_UNITS, increment: 1, allowZero: false }, rules || {});
    const text = String(value).trim().replace(',', '.');
    if (!/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(text)) throw new Error('Enter kilograms using up to two decimal places, for example 1.25.');
    const parts = text.split('.');
    const units = Number(parts[0] || '0') * UNITS_PER_KG + Number((parts[1] || '').padEnd(2, '0'));
    if (!Number.isSafeInteger(units) || units > MAX_UNITS) throw new Error('Please enter a smaller weight.');
    if (units === 0 && rule.allowZero) return 0;
    if (units < rule.min) throw new Error('The minimum weight is ' + formatKg(rule.min) + ' kg.');
    if (units > rule.max) throw new Error('Up to ' + formatKg(Math.max(0, rule.max)) + ' kg is currently available.');
    if (units % rule.increment !== 0) throw new Error('Choose weight in steps of ' + formatKg(rule.increment) + ' kg.');
    return units;
  }
  function priceForUnits(priceCents, units) {
    if (!Number.isSafeInteger(priceCents) || priceCents < 0 || !Number.isSafeInteger(units) || units < 0) throw new Error('Invalid price or quantity.');
    const amount = priceCents * units;
    if (!Number.isSafeInteger(amount)) throw new Error('This order amount is too large.');
    return amount;
  }
  function getRules(data, isKg) {
    const increment = positiveInt(data.ruleIncrement, 1);
    const merchantMin = isKg ? Math.ceil(positiveInt(data.minimumGrams, 10) / 10) : 1;
    const min = Math.ceil(Math.max(positiveInt(data.ruleMin, 1), merchantMin) / increment) * increment;
    let max = Math.min(positiveInt(data.ruleMax, MAX_UNITS), MAX_UNITS);
    if (isKg && data.stockTracked === 'true') max = Math.min(max, Math.max(0, Number(data.stockUnits) || 0));
    return { min, max: Math.floor(max / increment) * increment, increment };
  }
  function makeProperties(previous, units, token) {
    return {
      ...(previous || {}),
      Weight: formatKg(units) + ' kg',
      'Pricing unit': '10g; 100 units = 1kg',
      _hlm_weight_unit: '10g',
      _hlm_line_token: token
    };
  }
  function signature(properties) {
    const source = properties || {};
    return JSON.stringify(Object.keys(source).sort().map((key) => [key, source[key]]));
  }
  function locateLine(cart, entry) {
    const items = cart.items || [];
    const exact = items.find((item) => item.key === entry.key);
    if (exact) return exact;
    let candidates = items.filter((item) => String(item.variant_id) === String(entry.variantId));
    if (entry.token) candidates = candidates.filter((item) => item.properties && item.properties._hlm_line_token === entry.token);
    else candidates = candidates.filter((item) => signature(item.properties) === entry.propertySignature);
    if (candidates.length !== 1) throw new Error('The cart changed. Reload it before continuing.');
    return candidates[0];
  }
  function assertCartStock(entries) {
    const groups = new Map();
    entries.filter((entry) => entry.isKg && entry.units > 0).forEach((entry) => {
      const key = String(entry.variantId);
      const group = groups.get(key) || { total: 0, available: entry.rules.max };
      group.total += entry.units;
      group.available = Math.min(group.available, entry.rules.max);
      groups.set(key, group);
    });
    groups.forEach((group) => {
      if (group.total > group.available) throw new Error('Your cart exceeds the available weight for an item. Reduce its combined weight and try again.');
    });
  }
  function assertLineAvailable(data, units, isKg) {
    if (units > 0 && data.manualSoldOut === 'true') throw new Error('An item in your cart is now out of stock. Remove it before checkout.');
    if (units > 0 && data.priceUnavailable === 'true') throw new Error('An item in your cart does not have a valid price yet. Remove it before checkout.');
    if (isKg && units > 0 && data.kgValid !== 'true') throw new Error('Remove the old weight item and select its weight again before checkout.');
  }
  async function syncCart(entries, service) {
    assertCartStock(entries);
    let cart = await service.read();
    if (!Array.isArray(cart.items) || cart.items.length !== entries.length) throw new Error('The cart changed in another tab. Reload it before continuing.');
    for (const entry of entries) {
      const item = cart.items.find((line) => line.key === entry.key);
      if (!item || item.quantity !== entry.originalUnits) throw new Error('The cart changed in another tab. Reload it before continuing.');
      entry.propertySignature = signature(item.properties);
      if (entry.isKg && entry.units > 0 && (!entry.valid || item.grams !== 10 || item.properties?._hlm_weight_unit !== '10g')) throw new Error('Please remove the old weight item and choose its weight again on the product page.');
    }
    // Reduce quantities first so redistributing weight between repeated lines does not briefly exceed stock.
    // Revalidate every kg line through change.js, which checks stock. Do not use update.js for quantities.
    const changes = entries.filter((entry) => entry.isKg || entry.units !== entry.originalUnits)
      .sort((a, b) => Number(a.units >= a.originalUnits) - Number(b.units >= b.originalUnits));
    for (const entry of changes) {
      const item = locateLine(cart, entry);
      const payload = { id: item.key, quantity: entry.units };
      if (entry.isKg && entry.units > 0) payload.properties = makeProperties(item.properties, entry.units, entry.token);
      cart = await service.change(payload);
      if (!Array.isArray(cart.items)) throw new Error('Shopify did not return the updated cart. Reload it to review your quantities.');
      if (entry.units > 0) {
        // Discounts can change keys. A unique line token keeps repeated kg products separate.
        const updated = locateLine(cart, { ...entry, key: '' });
        if (updated.quantity !== entry.units) throw new Error('Shopify adjusted an item quantity. Reload the cart and review its weight.');
        if (entry.isKg && updated.properties?.Weight !== formatKg(entry.units) + ' kg') throw new Error('The weight label could not be confirmed. Reload the cart before checkout.');
        entry.key = updated.key;
      }
    }
    if (service.note !== undefined) cart = await service.updateNote(service.note);
    return cart;
  }
  function money(cents, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-NG', { style: 'currency', currency: currency || 'NGN' }).format(cents / 100);
  }
  function uniqueToken() {
    return window.crypto?.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }
  function baseUrl(root) {
    const path = root || window.Shopify?.routes?.root || '/';
    return path.endsWith('/') ? path : path + '/';
  }
  async function requestJson(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store',
        signal: controller.signal, headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
        ...(body === undefined ? {} : { body: JSON.stringify(body) })
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description : (data.message || 'The cart could not be updated.'));
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  function showError(node, text) {
    node.textContent = text || '';
    node.hidden = !text;
  }
  function initProduct(root) {
    if (root.dataset.kgBound) return;
    root.dataset.kgBound = 'true';
    const form = root.querySelector('.product-form');
    const input = root.querySelector('[data-kg-input]');
    const quantity = root.querySelector('[data-unit-quantity]');
    const weightProperty = root.querySelector('[data-weight-property]');
    const token = root.querySelector('[data-line-token]');
    const add = root.querySelector('[data-add-button]');
    const error = root.querySelector('[data-kg-error]');
    const rules = getRules(root.dataset, true);
    const price = Number(root.dataset.priceCents);
    const ready = root.dataset.kgReady === 'true' && root.dataset.stockOk === 'true' && root.dataset.manualSoldOut !== 'true' && Number.isSafeInteger(price) && price > 0 && rules.max >= rules.min;
    const controls = root.querySelectorAll('[data-kg-minus],[data-kg-plus],[data-kg-preset],[data-kg-input]');
    let busy = false;
    const refresh = () => {
      if (!ready || busy) return null;
      try {
        const units = parseKg(input.value, rules);
        input.setCustomValidity('');
        quantity.value = String(units);
        weightProperty.value = formatKg(units) + ' kg';
        root.querySelector('[data-kg-selected]').textContent = weightProperty.value;
        root.querySelector('[data-kg-total]').textContent = money(priceForUnits(price, units), root.dataset.currency);
        root.querySelectorAll('[data-kg-preset]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.kgPreset) * 100 === units)));
        root.querySelector('[data-kg-minus]').disabled = units <= rules.min;
        root.querySelector('[data-kg-plus]').disabled = units >= rules.max;
        showError(error, '');
        add.disabled = false;
        add.textContent = 'ADD ' + formatKg(units) + ' KG TO CART';
        return units;
      } catch (problem) {
        input.setCustomValidity(problem.message);
        showError(error, problem.message);
        add.disabled = true;
        root.querySelector('[data-kg-total]').textContent = '—';
        return null;
      }
    };
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy || !ready) return;
      const units = refresh();
      if (units === null) { input.reportValidity(); return; }
      busy = true;
      controls.forEach((control) => { control.disabled = true; });
      add.disabled = true; add.textContent = 'CHECKING WEIGHT…';
      let failure = '';
      try {
        const base = baseUrl(root.dataset.rootUrl);
        const cart = await requestJson(base + 'cart.js');
        const inCart = (cart.items || []).filter((item) => String(item.variant_id) === root.dataset.variantId).reduce((sum, item) => sum + item.quantity, 0);
        if (units + inCart > rules.max) throw new Error('Including the weight already in your cart, up to ' + formatKg(Math.max(0, rules.max - inCart)) + ' kg more is available.');
        token.value = uniqueToken();
        const properties = {};
        for (const [key, value] of new FormData(form).entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match) properties[match[1]] = value;
        }
        const result = await requestJson(base + 'cart/add.js', { items: [{ id: root.dataset.variantId, quantity: units, properties: makeProperties(properties, units, token.value) }] });
        if (!result.items || result.items[0]?.quantity !== units) throw new Error('Your cart quantity changed. Open your cart and review the weight.');
        const updatedCart = await requestJson(base + 'cart.js');
        if (window.HLMCartUI?.added) window.HLMCartUI.added(updatedCart, result.items[0]?.product_title || result.items[0]?.title);
        else document.dispatchEvent(new CustomEvent('hlm:cart-updated', { detail: { cart: updatedCart } }));
      } catch (problem) {
        failure = (problem.name === 'AbortError' ? 'The connection timed out.' : problem.message) + ' Please review your cart before trying again.';
      } finally {
        busy = false;
        controls.forEach((control) => { control.disabled = false; });
        refresh();
        root.querySelectorAll('[data-kg-preset]').forEach((button) => {
          try { parseKg(button.dataset.kgPreset, rules); } catch (_) { button.disabled = true; }
        });
        if (failure) {
          showError(error, failure + ' ');
          const link = document.createElement('a');
          link.href = root.dataset.cartUrl; link.textContent = 'View cart'; error.appendChild(link);
        }
      }
    });
    if (!ready) {
      const priceUnavailable = !Number.isSafeInteger(price) || price <= 0;
      add.textContent = priceUnavailable ? 'PRICE UNAVAILABLE' : 'CHOOSE WEIGHT';
      showError(error, priceUnavailable ? 'This product needs a valid price before it can be ordered.' : 'Online weight ordering is not ready for this older product setup. Please import the updated product file or contact us on WhatsApp.');
      return;
    }
    controls.forEach((control) => { control.disabled = false; });
    input.min = formatKg(rules.min); input.max = formatKg(rules.max); input.step = formatKg(rules.increment);
    input.value = formatKg(Math.max(rules.min, Math.min(rules.max, Math.floor(100 / rules.increment) * rules.increment)));
    root.querySelector('[data-kg-help]').textContent = 'Minimum ' + formatKg(rules.min) + ' kg. Enter up to two decimal places; the price updates automatically.';
    input.addEventListener('input', refresh);
    input.addEventListener('change', () => { const units = refresh(); if (units !== null) input.value = formatKg(units); });
    const buttonStep = Math.max(rules.increment, Math.round(positiveInt(root.dataset.buttonGrams, 250) / 10 / rules.increment) * rules.increment);
    root.querySelectorAll('[data-kg-minus],[data-kg-plus]').forEach((button) => button.addEventListener('click', () => {
      let units; try { units = parseKg(input.value, rules); } catch (_) { units = rules.min; }
      units += button.hasAttribute('data-kg-plus') ? buttonStep : -buttonStep;
      input.value = formatKg(Math.max(rules.min, Math.min(rules.max, units))); refresh();
    }));
    root.querySelectorAll('[data-kg-preset]').forEach((button) => {
      try { parseKg(button.dataset.kgPreset, rules); } catch (_) { button.disabled = true; }
      button.addEventListener('click', () => { input.value = button.dataset.kgPreset; refresh(); });
    });
    refresh();
  }
  function initCart(form) {
    if (form.dataset.kgBound) return;
    form.dataset.kgBound = 'true';
    const nodes = [...form.querySelectorAll('[data-cart-line]')];
    const error = form.querySelector('[data-cart-error]');
    const reload = form.querySelector('[data-cart-reload]');
    const update = form.querySelector('[data-cart-update]');
    const checkout = form.querySelector('[data-cart-checkout]');
    const currency = form.dataset.currency;
    let busy = false;
    let reloadRequired = false;
    const entries = () => nodes.map((node) => {
      const isKg = node.hasAttribute('data-kg-line');
      const input = node.querySelector(isKg ? '[data-cart-kg-input]' : '[data-cart-quantity-input]');
      const rules = getRules(node.dataset, isKg);
      let units;
      if (node.dataset.remove === 'true') units = 0;
      else if (isKg) units = parseKg(input.value, { ...rules, allowZero: true });
      else {
        const text = input.value.trim();
        units = /^\d+$/.test(text) ? Number(text) : NaN;
        if (!Number.isSafeInteger(units) || units > MAX_UNITS || (units > 0 && (units < rules.min || units > rules.max || units % rules.increment !== 0))) throw new Error('Enter a valid whole-number quantity for items sold by piece or pack.');
      }
      assertLineAvailable(node.dataset, units, isKg);
      return { node, isKg, valid: node.dataset.kgValid === 'true', key: node.dataset.key, token: node.dataset.lineToken, variantId: node.dataset.variantId, originalUnits: Number(node.dataset.units), units, rules };
    });
    const refresh = () => {
      if (busy || reloadRequired) return null;
      try {
        const list = entries(); assertCartStock(list);
        let delta = 0;
        list.forEach((entry) => {
          const changed = entry.units !== entry.originalUnits;
          const price = changed ? priceForUnits(Number(entry.node.dataset.priceCents), entry.units) : Number(entry.node.dataset.linePrice);
          delta += price - Number(entry.node.dataset.linePrice);
          entry.node.querySelector('[data-line-total]').textContent = money(price, currency);
          const hidden = entry.node.querySelector('[data-cart-unit-quantity]');
          if (hidden) hidden.value = String(entry.units);
        });
        form.querySelector('[data-cart-subtotal]').textContent = money(Math.max(0, Number(form.dataset.subtotal) + delta), currency);
        form.querySelector('[data-cart-estimate]').hidden = !list.some((entry) => entry.units !== entry.originalUnits);
        showError(error, ''); update.disabled = false; checkout.disabled = false;
        return list;
      } catch (problem) { showError(error, problem.message); update.disabled = true; checkout.disabled = true; return null; }
    };
    form.addEventListener('input', refresh);
    form.querySelectorAll('[data-cart-remove]').forEach((link) => link.addEventListener('click', (event) => {
      // Native removal remains available even when another line has invalid stock or an old weight.
      if (busy) event.preventDefault();
    }));
    form.addEventListener('submit', async (event) => {
      event.preventDefault(); if (busy || reloadRequired) return;
      const list = refresh(); if (!list) return;
      const goToCheckout = event.submitter === checkout;
      busy = true; update.disabled = true; checkout.disabled = true;
      form.setAttribute('aria-busy', 'true');
      form.querySelectorAll('input,textarea').forEach((input) => { input.readOnly = true; });
      try {
        const base = baseUrl(form.dataset.rootUrl);
        await syncCart(list, {
          read: () => requestJson(base + 'cart.js'),
          change: (payload) => requestJson(base + 'cart/change.js', payload),
          note: form.querySelector('[name="note"]').value,
          updateNote: (note) => requestJson(base + 'cart/update.js', { note })
        });
        if (goToCheckout) {
          const checkoutForm = document.createElement('form');
          checkoutForm.action = form.action; checkoutForm.method = 'post';
          const trigger = document.createElement('input');
          trigger.type = 'hidden'; trigger.name = 'checkout'; trigger.value = 'Checkout';
          checkoutForm.appendChild(trigger); document.body.appendChild(checkoutForm); checkoutForm.submit();
        } else window.location.reload();
      } catch (problem) {
        reloadRequired = true;
        showError(error, (problem.name === 'AbortError' ? 'The connection timed out.' : problem.message) + ' Your cart may have been partly updated. Reload it to review the confirmed quantities.');
        reload.hidden = false;
      } finally {
        busy = false; form.removeAttribute('aria-busy');
        if (!reloadRequired) refresh();
        else form.querySelectorAll('input,textarea').forEach((input) => { input.readOnly = true; });
      }
    });
    refresh();
  }
  return { UNITS_PER_KG, MAX_UNITS, weightCollectionUrl, formatKg, parseKg, priceForUnits, getRules, makeProperties, signature, locateLine, assertCartStock, assertLineAvailable, syncCart, initProduct, initCart };
});
